import {
  Button,
  Input,
  InputDay,
  InputSelect,
  InputWrapper,
} from '@vrplatform/components';
import {
  captureSentryError,
  showApiErrorNotification,
  useFinalyticConnectionQuery,
  useApiMutation,
  useInfiniteQuery,
  useQuery,
  useTeam,
} from '@vrplatform/data';
import type { SelectItem } from '@vrplatform/components';
import { showWarnNotification } from '@vrplatform/theme';
import { type Maybe, day, sortBy } from '@vrplatform/utils';
import { Box, Collapse, Group, Modal, Stack } from '@mantine/core';
import {
  getListingName,
  orderByListing,
  whereListings,
} from '@vrplatform/ui-common';
import { useCallback, useState } from 'react';
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext as useFormContextApi,
  useWatch,
} from 'react-hook-form';
import { useReservationDetailDrawer } from '../../drawers';

type Props = {
  opened: boolean | string;
  closeModal: () => void;
  openExtractModal: (() => void) | undefined;
};

type FormInputs = {
  listingId: string;
  guestName: string;
  bookedAt: string;
  checkIn: string;
  checkOut: string;
  confirmationCode: Maybe<string>;
  status: 'booked' | 'canceled';
  bookingPlatform: string | null;
  canceledAt: Maybe<string>;
};

function useFormContext() {
  return useFormContextApi<FormInputs>();
}

export const ReservationCreateModal = ({
  opened,
  closeModal,
  openExtractModal,
}: Props) => {
  const { finalyticConnectionId, loading: loadingFinalyticConnection } =
    useFinalyticConnectionQuery();

  const { mutateAsync } = useApiMutation('post', '/reservations');

  const methods = useForm<FormInputs>({
    shouldUnregister: true,
    defaultValues: {
      status: 'booked',
    },
  });

  const { open: openReservationDetailDrawer } = useReservationDetailDrawer();

  const submit = useCallback(
    async (data: FormInputs) => {
      if (!finalyticConnectionId) {
        captureSentryError('Connection to delete missing');
        showWarnNotification({
          title: 'Missing connection',
          message:
            'Connection to delete missing. Please reach out to support if the issue persists.',
        });

        return;
      }

      return await mutateAsync({
        body: {
          connectionId: finalyticConnectionId,
          lines: [],
          confirmationCode: data.confirmationCode?.trim(),
          listingId: data.listingId,
          bookedAt: data.bookedAt,
          checkIn: data.checkIn,
          checkOut: data.checkOut,
          guestName: data.guestName,
          bookingPlatform: data.bookingPlatform,
          status: data.status,
          cancelledAt: data.status === 'canceled' ? data.canceledAt : null,
        },
      })
        .then((reservation) => {
          closeModal();
          openReservationDetailDrawer(reservation.data.id, 'overview');
        })
        .catch((err) => {
          showApiErrorNotification({
            error: err,
            title: 'Failed to create reservation',
            defaultMessage:
              'We failed to create the reservation. Please reach out to support if the issue persists.',
          });
        });
    },
    [
      mutateAsync,
      finalyticConnectionId,
      closeModal,
      openReservationDetailDrawer,
    ]
  );

  return (
    <Modal
      opened={!!opened}
      onClose={closeModal}
      size={400}
      title={'Add Reservation'}
      centered
      styles={{
        title: {
          fontWeight: 500,
        },
      }}
    >
      <FormProvider {...methods}>
        <Box
          component="form"
          onSubmit={methods.handleSubmit(submit)}
          onReset={closeModal}
        >
          <Stack mb={'xl'}>
            <Listing />

            <Text
              name="guestName"
              label="Guest name"
              placeholder="Enter a guest name"
              required="Guest name is required"
            />

            <Date
              name="bookedAt"
              label="Booked at"
              placeholder="Enter a booked at"
              required="Booked at is required"
            />

            <Group
              justify="space-between"
              align="flex-start"
              wrap="nowrap"
              sx={{
                '> *': {
                  flex: 1,
                  flexShrink: 0,
                },
              }}
            >
              <Date
                name="checkIn"
                label="Check in"
                placeholder="Enter a check in"
                required="Check in is required"
              />
              <Date
                name="checkOut"
                label="Check out"
                placeholder="Enter a check out"
                required="Check out is required"
              />
            </Group>

            <Text
              name="confirmationCode"
              label="Confirmation code"
              placeholder="Enter a confirmation code"
              required={false}
            />

            <BookingChannelSelect />

            <Status />
          </Stack>
          <Group justify="right" my={5}>
            {openExtractModal && (
              <Button
                type="button"
                disabled={methods.formState.isSubmitting}
                sx={{
                  marginRight: 'auto',
                }}
                onClick={openExtractModal}
              >
                Add by code
              </Button>
            )}

            <Button type="reset" disabled={methods.formState.isSubmitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              loading={
                methods.formState.isSubmitting || loadingFinalyticConnection
              }
              disabled={loadingFinalyticConnection}
            >
              Submit
            </Button>
          </Group>
        </Box>
      </FormProvider>
    </Modal>
  );
};

const Text = ({
  name,
  label,
  placeholder,
  required,
}: {
  name: keyof FormInputs;
  label: string;
  placeholder: string;
  required: false | string;
}) => {
  const methods = useFormContext();

  return (
    <Controller
      control={methods.control}
      name={name}
      rules={{
        validate: (value) => (!required ? true : !!value?.trim() || required),
      }}
      render={({ field, fieldState: { error } }) => (
        <InputWrapper
          id={field.name}
          label={label}
          required={!!required}
          error={error?.message}
        >
          <Input
            name={field.name}
            placeholder={placeholder}
            type="text"
            value={field.value || ''}
            setValue={field.onChange}
            error={!!error}
            data-autofocus
          />
        </InputWrapper>
      )}
    />
  );
};

const Date = ({
  name,
  label,
  placeholder,
  required,
}: {
  name: keyof FormInputs;
  label: string;
  placeholder: string;
  required: false | string;
}) => {
  const methods = useFormContext();

  const isCheckIn = name === 'checkIn';
  const isCheckOut = name === 'checkOut';

  const checkIn = useWatch({
    control: methods.control,
    name: 'checkIn',
  });
  const checkOut = useWatch({
    control: methods.control,
    name: 'checkOut',
  });

  return (
    <Controller
      control={methods.control}
      name={name}
      rules={{
        required,
      }}
      render={({ field, fieldState: { error } }) => (
        <InputWrapper
          id={field.name}
          label={label}
          required={!!required}
          error={error?.message}
        >
          <InputDay
            error={!!error}
            value={field.value ? day(field.value).toDate() : null}
            onChange={(value) =>
              field.onChange(value ? day(value).yyyymmdd() : null)
            }
            minDate={
              isCheckOut && checkIn
                ? day(checkIn).add(1, 'day').toDate()
                : undefined
            }
            maxDate={
              isCheckIn && checkOut
                ? day(checkOut).subtract(1, 'day').toDate()
                : undefined
            }
            placeholder={placeholder}
            clearable={isCheckIn || isCheckOut}
          />
        </InputWrapper>
      )}
    />
  );
};

const STATUS_OPTIONS: SelectItem<FormInputs['status']>[] = [
  {
    label: 'Booked',
    value: 'booked',
    icon: (
      <Box
        w={8}
        h={8}
        sx={(theme) => ({
          backgroundColor: theme.colors.green[6],
          borderRadius: '50%',
          alignSelf: 'center',
        })}
      />
    ),
  },
  {
    label: 'Canceled',
    value: 'canceled',
    icon: (
      <Box
        w={8}
        h={8}
        sx={(theme) => ({
          backgroundColor: theme.colors.orange[6],
          borderRadius: '50%',
          alignSelf: 'center',
        })}
      />
    ),
  },
];

const Status = () => {
  const methods = useFormContext();

  const status =
    useWatch({
      control: methods.control,
      name: 'status',
    }) || 'booked';

  return (
    <>
      <Controller
        control={methods.control}
        name={'status'}
        defaultValue={'booked'}
        rules={{
          required: 'Status is required',
        }}
        render={({ field, fieldState: { error } }) => (
          <InputWrapper id={field.name} label={'Status'} error={error?.message}>
            <InputSelect
              name={field.name}
              type="single"
              setValue={(v) => v?.value && field.onChange(v.value)}
              value={
                STATUS_OPTIONS.find((o) => o.value === field.value) || null
              }
              data={{
                options: STATUS_OPTIONS,
              }}
              dropdownProps={{
                hideSearch: true,
              }}
              inputProps={{
                error: !!error,
              }}
            />
          </InputWrapper>
        )}
      />
      <Collapse in={status === 'canceled'}>
        <Date
          name="canceledAt"
          label="Canceled at"
          placeholder="Enter a canceled at"
          required={status === 'canceled' ? 'Canceled at is required' : false}
        />
      </Collapse>
    </>
  );
};

const Listing = () => {
  const methods = useFormContext();

  const [{ id: teamId }] = useTeam();

  const [search, setSearch] = useState('');

  const queryData = useInfiniteQuery(
    (q, { teamId, search }, { limit, offset }) => {
      if (!teamId)
        return {
          list: [],
          aggregate: 0,
        };

      const where = whereListings({
        search,
        tenantId: teamId,
        dashboard: 'propertyManager',
        GL: true,
        ownerPortalContactId: null,
      });

      const list = q
        .listings({
          where,
          order_by: [orderByListing],
          limit,
          offset,
        })
        .map<SelectItem>((res) => ({
          value: res.id,
          label: getListingName(res),
        }));

      const aggregate = q.listingAggregate({ where }).aggregate?.count() || 0;

      return {
        list,
        aggregate,
      };
    },
    {
      queryKey: ['listings'],
      variables: {
        teamId,
        search: search?.trim(),
      },
    }
  );

  return (
    <Controller
      control={methods.control}
      name={'listingId'}
      rules={{
        required: 'Listing is required',
      }}
      render={({ field, fieldState }) => {
        const { data, isLoading: loading } = useQuery(
          (q, args) => {
            if (!args.value) return null;

            return q
              .listings({
                where: {
                  id: { _eq: args.value },
                },
                limit: 1,
                order_by: [orderByListing],
              })
              .map<SelectItem>((item) => ({
                label: getListingName(item),
                value: item.id!,
              }))[0];
          },
          {
            keepPreviousData: true,
            variables: {
              value: field.value,
            },
          }
        );

        const active = field.value ? data || null : null;

        return (
          <InputWrapper
            label="Listing"
            required
            error={fieldState.error?.message}
          >
            <InputSelect
              type="single"
              value={active}
              data-testid={field.name}
              name={field.name}
              setValue={(v) => field.onChange(v?.value || null)}
              infiniteData={{ ...queryData, setSearch }}
              inputProps={{
                error: !!fieldState.error,
                placeholder: 'Select listing',
                loadingQuery: loading,
                withClearButton: false,
                lineClamp: 1,
              }}
              dropdownProps={{
                width: 350,
              }}
            />
          </InputWrapper>
        );
      }}
    />
  );
};

const BookingChannelSelect = () => {
  const methods = useFormContext();

  const [{ id: teamId }] = useTeam();

  const {
    data,
    isLoading: loadingQuery,
    error,
  } = useQuery(
    (q, args) => {
      if (!args.teamId)
        return {
          bookingChannels: [],
        };

      const bookingChannels = q
        .bookingChannels({
          distinct_on: ['channelRef'],
          order_by: [{ channelRef: 'asc_nulls_last' }],
          where: {
            channelRef: { _is_null: false },
            reservations: {
              tenantId: { _eq: args.teamId },
            },
          },
        })
        .map<SelectItem>((channel) => {
          const ref = channel.channelRef || '-';

          return {
            label: ref.trim(),
            value: ref,
          };
        });

      return {
        bookingChannels: sortBy(bookingChannels, 'label'),
      };
    },
    {
      queryKey: 'paymentLineClassifications',
      variables: {
        teamId,
      },
    }
  );

  const bookingChannels = data?.bookingChannels ?? [];

  return (
    <Controller
      control={methods.control}
      name="bookingPlatform"
      render={({ field, fieldState }) => {
        const selectValue =
          bookingChannels?.find((o) => o.value === field.value) || null;

        return (
          <InputWrapper
            label="Booking Channel"
            error={fieldState.error?.message}
          >
            <InputSelect
              type="single"
              value={selectValue}
              data={{
                options: bookingChannels || [],
                loading: loadingQuery,
                error,
              }}
              setValue={(value) => {
                field.onChange(value?.value ?? null);
              }}
              inputProps={{
                error: !!fieldState.error,
                placeholder: 'Select booking channel',
                withClearButton: true,
              }}
              dropdownProps={{
                withinPortal: true,
              }}
            />
          </InputWrapper>
        );
      }}
    />
  );
};
